import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../../services/api';
import { toast } from '../../hooks/useToast';
import { BudgetSkeleton } from '../Skeleton';
import {
  Plus, Trash2, TrendingUp, TrendingDown,
  Calendar, Tag, CreditCard, Pencil, X,
  ChevronDown, ChevronRight, Wallet,
  FileText, Bell, Check, Clock, RepeatIcon, Landmark,
  ArrowRightLeft, Eye, EyeOff,
} from 'lucide-react';

interface BudgetCategory {
  id: number;
  name: string;
  color: string;
  icon?: string;
}

interface BudgetCard {
  id: number;
  name: string;
  bank: string;
  last4: string;
  color: string;
  icon: string;
  balance: number;
  is_default: number;
  is_credit: number;
  currency: string;
}

interface BudgetEntry {
  id: number;
  amount: number;
  type: 'income' | 'expense';
  category_id: number | null;
  category_name: string | null;
  category_color: string | null;
  card_id: number | null;
  card_name: string | null;
  card_last4: string | null;
  card_color: string | null;
  card_bank: string | null;
  note_id: number | null;
  note_title: string | null;
  description: string;
  date: string;
  is_excluded: number;
  recurring: string;
}

interface PlannedExpense {
  id: number;
  title: string;
  amount: number;
  category_id: number | null;
  category_name: string | null;
  category_color: string | null;
  card_id: number | null;
  card_name: string | null;
  card_last4: string | null;
  card_color: string | null;
  day_of_month: number;
  is_recurring: number;
  is_active: number;
  next_due: string;
  last_paid: string | null;
  note: string;
  period_type: string;
  period_months: number;
}

interface NoteOption { id: number; title: string; }

interface BudgetCredit {
  id: number;
  title: string;
  total_amount: number;
  paid_amount: number;
  monthly_payment: number;
  start_date: string | null;
  end_date: string | null;
  interest_rate: number | null;
  card_id: number | null;
  card_name: string | null;
  card_last4: string | null;
  card_color: string | null;
  note: string;
  is_active: number;
  day_of_month: number | null;
}

interface SummaryData {
  totals: { type: string; total: number }[];
  byCategory: { name: string; color: string; type: string; total: number }[];
  byCard: { card_id: number; name: string; last4: string; color: string; bank: string; total_income: number; total_expense: number }[];
}

const CARD_GRADIENT_PRESETS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
  'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
];

const CATEGORY_ICONS = [
  '🍔', '🚗', '🏠', '💊', '🎮', '✈️', '🛒', '💼',
  '📚', '🐾', '👕', '💸', '🍺', '⚡', '💰', '📱',
  '💻', '🎁', '🚂', '🎧', '🏋️', '🌟', '🎪', '💡',
  '🔧', '☕', '🍕', '💈', '🎨', '📌',
];

type QuickPeriod = 'today' | 'week' | 'month' | 'year' | 'custom';
type Tab = 'overview' | 'cards' | 'categories' | 'planned' | 'credits';

interface BudgetViewProps {
  onNavigateToNote?: (noteId: number) => void;
}

export function BudgetView({ onNavigateToNote }: BudgetViewProps) {
  const [entries, setEntries] = useState<BudgetEntry[]>([]);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [cards, setCards] = useState<BudgetCard[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [planned, setPlanned] = useState<PlannedExpense[]>([]);
  const [credits, setCredits] = useState<BudgetCredit[]>([]);
  const [goals, setGoals] = useState<{ id: number; category_id: number; monthly_limit: number }[]>([]);
  const [goalInputs, setGoalInputs] = useState<Record<number, string>>({});
  const [noteOptions, setNoteOptions] = useState<NoteOption[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [quickPeriod, setQuickPeriod] = useState<QuickPeriod>('month');
  const [filterCard, setFilterCard] = useState<string>('');
  const [loaded, setLoaded] = useState(false);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Forms
  const [showAdd, setShowAdd] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCardId, setEditingCardId] = useState<number | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);

  // Entry form
  const [newAmount, setNewAmount] = useState('');
  const [newType, setNewType] = useState<'income' | 'expense'>('expense');
  const [newCat, setNewCat] = useState('');
  const [newCard, setNewCard] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newDate, setNewDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newNoteId, setNewNoteId] = useState('');
  const [newRecurring, setNewRecurring] = useState('none');

  // Card form
  const [cardName, setCardName] = useState('');
  const [cardBank, setCardBank] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [cardColor, setCardColor] = useState(CARD_GRADIENT_PRESETS[0]);
  const [cardBalance, setCardBalance] = useState('');
  const [cardIsCredit, setCardIsCredit] = useState(false);
  const [cardCurrency, setCardCurrency] = useState('RUB');

  // Category form
  const [catName, setCatName] = useState('');
  const [catColor, setCatColor] = useState('#6366f1');
  const [catIcon, setCatIcon] = useState('');
  const [editingCatId, setEditingCatId] = useState<number | null>(null);

  // Credit form
  const [showPlannedForm, setShowPlannedForm] = useState(false);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [editingCreditId, setEditingCreditId] = useState<number | null>(null);
  const [creditTitle, setCreditTitle] = useState('');
  const [creditTotal, setCreditTotal] = useState('');
  const [creditPaid, setCreditPaid] = useState('0');
  const [creditMonthly, setCreditMonthly] = useState('');
  const [creditEndDate, setCreditEndDate] = useState('');
  const [creditRate, setCreditRate] = useState('');
  const [creditCard, setCreditCard] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [plannedTitle, setPlannedTitle] = useState('');
  const [plannedAmount, setPlannedAmount] = useState('');
  const [plannedCat, setPlannedCat] = useState('');
  const [plannedCard, setPlannedCard] = useState('');
  const [plannedDay, setPlannedDay] = useState('1');
  const [plannedRecurring, setPlannedRecurring] = useState(true);
  const [plannedNote, setPlannedNote] = useState('');
  const [plannedPeriodType, setPlannedPeriodType] = useState('monthly');
  const [plannedPeriodMonths, setPlannedPeriodMonths] = useState('1');
  const [editingPlannedId, setEditingPlannedId] = useState<number | null>(null);

  // Transfer form
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  // Credit day_of_month
  const [creditDayOfMonth, setCreditDayOfMonth] = useState('');

  // Quick period handler
  const applyQuickPeriod = (p: QuickPeriod) => {
    setQuickPeriod(p);
    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    let from: string;
    switch (p) {
      case 'today': from = to; break;
      case 'week': { const d = new Date(); d.setDate(d.getDate() - 7); from = d.toISOString().slice(0, 10); break; }
      case 'month': { const d = new Date(); d.setDate(1); from = d.toISOString().slice(0, 10); break; }
      case 'year': { const d = new Date(); d.setMonth(0, 1); from = d.toISOString().slice(0, 10); break; }
      default: return;
    }
    setDateFrom(from);
    setDateTo(to);
  };

  const load = useCallback(async () => {
    const safeFetch = async (url: string) => {
      try { const r = await apiFetch(url); return await r.json(); }
      catch { return { success: false }; }
    };
    const [eData, cData, sData, crData, pData, nData, credData, goalsData] = await Promise.all([
      safeFetch(`/api/budget/entries?from=${dateFrom}&to=${dateTo}`),
      safeFetch('/api/budget/categories'),
      safeFetch(`/api/budget/summary?from=${dateFrom}&to=${dateTo}`),
      safeFetch('/api/budget/cards'),
      safeFetch('/api/budget/planned'),
      safeFetch('/api/notes'),
      safeFetch('/api/budget/credits'),
      safeFetch('/api/budget/goals'),
    ]);
    if (eData.success) setEntries(eData.data);
    if (cData.success) setCategories(cData.data);
    if (sData.success) setSummary(sData.data);
    if (crData.success) setCards(crData.data);
    if (pData.success) setPlanned(pData.data);
    if (nData.success) setNoteOptions(nData.data.map((n: { id: number; title: string }) => ({ id: n.id, title: n.title || `Заметка #${n.id}` })));
    if (credData.success) setCredits(credData.data);
    if (goalsData.success) {
      setGoals(goalsData.data);
      const inp: Record<number, string> = {};
      for (const g of goalsData.data as { category_id: number; monthly_limit: number }[]) {
        inp[g.category_id] = String(g.monthly_limit);
      }
      setGoalInputs(inp);
    }
    if (!eData.success && !cData.success && !sData.success && !crData.success) {
      toast('Ошибка загрузки бюджета', 'error');
    }

    // Check due planned expenses (notifications)
    if (pData.success) {
      const today = new Date().toISOString().slice(0, 10);
      const due = (pData.data as PlannedExpense[]).filter(p => p.next_due <= today);
      if (due.length > 0) {
        toast(`Планируемые расходы: ${due.length} подписок к оплате!`, 'info');
      }
    }
    // Check due credits (day_of_month notifications)
    if (credData.success) {
      const todayDay = new Date().getDate();
      const dueCredits = (credData.data as BudgetCredit[]).filter(c => c.day_of_month !== null && c.day_of_month === todayDay);
      if (dueCredits.length > 0) {
        toast(`Кредиты: ${dueCredits.length} платежей сегодня!`, 'info');
      }
    }
    setLoaded(true);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('app:refresh', handler);
    return () => window.removeEventListener('app:refresh', handler);
  }, [load]);

  // Entry CRUD
  const addEntry = async () => {
    const amount = parseFloat(newAmount);
    if (!amount || isNaN(amount)) return;
    try {
      await apiFetch('/api/budget/entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount, type: newType,
          category_id: newCat ? parseInt(newCat) : undefined,
          card_id: newCard ? parseInt(newCard) : undefined,
          note_id: newNoteId ? parseInt(newNoteId) : undefined,
          description: newDesc, date: newDate,
          recurring: newRecurring,
        }),
      });
      setNewAmount(''); setNewDesc(''); setNewNoteId(''); setNewRecurring('none'); setShowAdd(false);
      toast('Запись добавлена');
      await load();
    } catch { toast('Ошибка добавления', 'error'); }
  };

  const deleteEntry = async (id: number) => {
    if (!confirm('Удалить запись?')) return;
    await apiFetch(`/api/budget/entries/${id}`, { method: 'DELETE' });
    toast('Запись удалена');
    await load();
  };

  const updateEntry = async (id: number, data: Record<string, unknown>) => {
    await apiFetch(`/api/budget/entries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    setEditingEntryId(null);
    await load();
  };

  // Transfer between cards
  const doTransfer = async () => {
    const amount = parseFloat(transferAmount);
    if (!transferFrom || !transferTo || !amount || amount <= 0) { toast('Заполните все поля перевода', 'error'); return; }
    try {
      await apiFetch('/api/budget/cards/transfer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_card_id: parseInt(transferFrom), to_card_id: parseInt(transferTo), amount }),
      });
      toast(`Перевод ${fmt(amount)} ₽ выполнен`);
      setShowTransfer(false); setTransferFrom(''); setTransferTo(''); setTransferAmount('');
      await load();
    } catch { toast('Ошибка перевода', 'error'); }
  };

  // Card CRUD
  const saveCard = async () => {
    if (!cardName.trim()) return;
    const body = {
      name: cardName.trim(), bank: cardBank.trim(),
      last4: cardLast4.trim(), color: cardColor,
      balance: parseFloat(cardBalance) || 0,
      is_credit: cardIsCredit ? 1 : 0,
      currency: cardCurrency,
    };
    try {
      if (editingCardId) {
        await apiFetch(`/api/budget/cards/${editingCardId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        toast('Карта обновлена');
      } else {
        await apiFetch('/api/budget/cards', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        toast('Карта добавлена');
      }
      resetCardForm();
      await load();
    } catch { toast('Ошибка сохранения карты', 'error'); }
  };

  const deleteCard = async (id: number) => {
    if (!confirm('Удалить карту?')) return;
    await apiFetch(`/api/budget/cards/${id}`, { method: 'DELETE' });
    toast('Карта удалена');
    await load();
  };

  const editCard = (c: BudgetCard) => {
    setEditingCardId(c.id);
    setCardName(c.name);
    setCardBank(c.bank);
    setCardLast4(c.last4);
    setCardColor(c.color);
    setCardBalance(String(c.balance));
    setCardIsCredit(!!c.is_credit);
    setCardCurrency(c.currency || 'RUB');
    setShowCardForm(true);
  };

  const resetCardForm = () => {
    setShowCardForm(false);
    setEditingCardId(null);
    setCardName(''); setCardBank(''); setCardLast4('');
    setCardColor(CARD_GRADIENT_PRESETS[0]); setCardBalance(''); setCardIsCredit(false); setCardCurrency('RUB');
  };

  // Category CRUD
  const addCategory = async () => {
    if (!catName.trim()) return;
    if (editingCatId) {
      await apiFetch(`/api/budget/categories/${editingCatId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: catName.trim(), color: catColor, icon: catIcon }),
      });
      toast('Категория обновлена');
      setEditingCatId(null);
    } else {
      await apiFetch('/api/budget/categories', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: catName.trim(), color: catColor, icon: catIcon }),
      });
      toast('Категория добавлена');
    }
    setCatName(''); setCatColor('#6366f1'); setCatIcon(''); setShowCatForm(false);
    await load();
  };

  const editCategory = (c: BudgetCategory) => {
    setEditingCatId(c.id);
    setCatName(c.name);
    setCatColor(c.color);
    setCatIcon(c.icon || '');
    setShowCatForm(true);
  };

  const deleteCategory = async (id: number) => {
    await apiFetch(`/api/budget/categories/${id}`, { method: 'DELETE' });
    toast('Категория удалена');
    await load();
  };

  // Credit CRUD
  const saveCredit = async () => {
    if (!creditTitle.trim()) { toast('Введите название кредита', 'error'); return; }
    if (!creditTotal || parseFloat(creditTotal) <= 0) { toast('Введите общую сумму кредита', 'error'); return; }
    const body = {
      title: creditTitle.trim(),
      total_amount: parseFloat(creditTotal),
      paid_amount: parseFloat(creditPaid) || 0,
      monthly_payment: parseFloat(creditMonthly) || 0,
      end_date: creditEndDate || undefined,
      interest_rate: creditRate ? parseFloat(creditRate) : undefined,
      card_id: creditCard ? parseInt(creditCard) : undefined,
      note: creditNote,
      day_of_month: creditDayOfMonth ? parseInt(creditDayOfMonth) : undefined,
    };
    try {
      if (editingCreditId) {
        await apiFetch(`/api/budget/credits/${editingCreditId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        toast('Кредит обновлён');
      } else {
        await apiFetch('/api/budget/credits', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        toast('Кредит добавлен');
      }
      resetCreditForm();
      await load();
    } catch { toast('Ошибка сохранения', 'error'); }
  };

  const payCreditInstallment = async (id: number) => {
    const res = await apiFetch(`/api/budget/credits/${id}/pay`, { method: 'POST' });
    const data = await res.json();
    if (data.data?.finished) toast('Кредит полностью погашен! ✅');
    else toast(`Взнос ${fmt(data.data?.payment ?? 0)} ₽ оплачен`);
    await load();
  };

  const editCredit = (c: BudgetCredit) => {
    setEditingCreditId(c.id);
    setCreditTitle(c.title);
    setCreditTotal(String(c.total_amount));
    setCreditPaid(String(c.paid_amount));
    setCreditMonthly(String(c.monthly_payment));
    setCreditEndDate(c.end_date || '');
    setCreditRate(c.interest_rate !== null ? String(c.interest_rate) : '');
    setCreditCard(c.card_id ? String(c.card_id) : '');
    setCreditNote(c.note || '');
    setCreditDayOfMonth(c.day_of_month ? String(c.day_of_month) : '');
    setShowCreditForm(true);
  };

  const deleteCredit = async (id: number) => {
    if (!confirm('Удалить кредит?')) return;
    await apiFetch(`/api/budget/credits/${id}`, { method: 'DELETE' });
    toast('Кредит удален');
    await load();
  };

  const resetCreditForm = () => {
    setShowCreditForm(false); setEditingCreditId(null);
    setCreditTitle(''); setCreditTotal(''); setCreditPaid('0'); setCreditMonthly('');
    setCreditEndDate(''); setCreditRate(''); setCreditCard(''); setCreditNote('');
    setCreditDayOfMonth('');
  };

  // Planned CRUD
  const computeNextDueFromDay = (day: number): string => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();
    if (now.getDate() > day) {
      month += 1;
      if (month > 11) { month = 0; year += 1; }
    }
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const d = Math.min(day, daysInMonth);
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const savePlanned = async () => {
    if (!plannedTitle.trim()) { toast('Введите название подписки', 'error'); return; }
    if (!plannedAmount || parseFloat(plannedAmount) <= 0) { toast('Введите сумму больше 0', 'error'); return; }
    const day = parseInt(plannedDay) || 1;
    const nextDue = computeNextDueFromDay(day);
    const body = {
      title: plannedTitle.trim(),
      amount: parseFloat(plannedAmount),
      category_id: plannedCat ? parseInt(plannedCat) : undefined,
      card_id: plannedCard ? parseInt(plannedCard) : undefined,
      day_of_month: day,
      is_recurring: plannedRecurring ? 1 : 0,
      next_due: nextDue,
      note: plannedNote,
      period_type: plannedPeriodType,
      period_months: parseInt(plannedPeriodMonths) || 1,
    };
    try {
      if (editingPlannedId) {
        await apiFetch(`/api/budget/planned/${editingPlannedId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        toast('Подписка обновлена');
      } else {
        await apiFetch('/api/budget/planned', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        toast('Подписка добавлена');
      }
      resetPlannedForm();
      await load();
    } catch { toast('Ошибка сохранения', 'error'); }
  };

  const markPaid = async (id: number) => {
    await apiFetch(`/api/budget/planned/${id}/pay`, { method: 'POST' });
    toast('Оплачено ✓');
    await load();
  };

  const deletePlanned = async (id: number) => {
    if (!confirm('Удалить подписку?')) return;
    await apiFetch(`/api/budget/planned/${id}`, { method: 'DELETE' });
    toast('Подписка удалена');
    await load();
  };

  const editPlanned = (p: PlannedExpense) => {
    setEditingPlannedId(p.id);
    setPlannedTitle(p.title);
    setPlannedAmount(String(p.amount));
    setPlannedCat(p.category_id ? String(p.category_id) : '');
    setPlannedCard(p.card_id ? String(p.card_id) : '');
    setPlannedDay(String(p.day_of_month));
    setPlannedRecurring(!!p.is_recurring);
    setPlannedNote(p.note || '');
    setPlannedPeriodType(p.period_type || 'monthly');
    setPlannedPeriodMonths(String(p.period_months || 1));
    setShowPlannedForm(true);
  };

  const resetPlannedForm = () => {
    setShowPlannedForm(false);
    setEditingPlannedId(null);
    setPlannedTitle(''); setPlannedAmount(''); setPlannedCat(''); setPlannedCard('');
    setPlannedDay('1'); setPlannedRecurring(true); setPlannedNote(''); setPlannedPeriodType('monthly'); setPlannedPeriodMonths('1');
  };

  // Goals handlers
  const saveGoal = async (categoryId: number) => {
    const val = parseFloat(goalInputs[categoryId] || '0');
    if (isNaN(val) || val < 0) return;
    try {
      await apiFetch('/api/budget/goals', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ category_id: categoryId, monthly_limit: val }) });
      await load();
      toast('Лимит сохранён');
    } catch { toast('Ошибка', 'error'); }
  };

  const deleteGoal = async (categoryId: number) => {
    try {
      await apiFetch(`/api/budget/goals/${categoryId}`, { method: 'DELETE' });
      setGoals(g => g.filter(x => x.category_id !== categoryId));
      setGoalInputs(prev => { const n = { ...prev }; delete n[categoryId]; return n; });
      toast('Лимит удалён');
    } catch { toast('Ошибка', 'error'); }
  };

  // Calculated values
  const exportCSV = () => {
    const rows = [
      ['Дата', 'Тип', 'Сумма', 'Категория', 'Счёт', 'Описание'],
      ...filteredEntries.map(e => [
        e.date,
        e.type === 'income' ? 'Доход' : 'Расход',
        e.amount.toString(),
        e.category_name || '',
        e.card_name || '',
        e.description || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `budget_${dateFrom}_${dateTo}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const totalIncome = useMemo(() => summary?.totals.find(t => t.type === 'income')?.total ?? 0, [summary]);
  const totalExpense = useMemo(() => summary?.totals.find(t => t.type === 'expense')?.total ?? 0, [summary]);
  // Balance = total of non-credit card balances (credit cards excluded)
  const balance = useMemo(() => cards.filter(c => !c.is_credit).reduce((s, c) => s + (c.balance || 0), 0), [cards]);
  const creditBalance = useMemo(() => cards.filter(c => !!c.is_credit).reduce((s, c) => s + (c.balance || 0), 0), [cards]);

  const currencySymbol = (cur?: string) => {
    switch (cur) { case 'USD': return '$'; case 'EUR': return '€'; default: return '₽'; }
  };

  // Filtered entries by card
  const filteredEntries = useMemo(() => {
    if (!filterCard) return entries;
    return entries.filter(e => String(e.card_id) === filterCard);
  }, [entries, filterCard]);

  const fmt = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  // Stats
  const daysInPeriod = Math.max(1, Math.ceil((new Date(dateTo).getTime() - new Date(dateFrom).getTime()) / 86400000) + 1);
  const avgExpensePerDay = totalExpense / daysInPeriod;
  const topCategory = useMemo(() => {
    if (!summary) return null;
    const exp = summary.byCategory.filter(c => c.total > 0);
    if (exp.length === 0) return null;
    return exp.reduce((a, b) => a.total > b.total ? a : b);
  }, [summary]);

  // Daily chart data: group entries by date, compute income/expense per day
  const dailyChartData = useMemo(() => {
    const map: Record<string, { income: number; expense: number }> = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = { income: 0, expense: 0 };
      if (e.type === 'income') map[e.date].income += e.amount;
      else map[e.date].expense += e.amount;
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([date, v]) => ({ date, ...v }));
  }, [entries]);

  // Due planned count (for tab badge)
  const duePlannedCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return planned.filter(p => p.next_due <= today).length;
  }, [planned]);

  // Total credit debt
  const totalCreditDebt = useMemo(() => {
    return credits.reduce((sum, c) => sum + Math.max(0, c.total_amount - c.paid_amount), 0);
  }, [credits]);

  // Pie chart
  const pieData = useMemo(() => {
    if (!summary) return [];
    const expCats = summary.byCategory.filter(c => c.total > 0);
    const total = expCats.reduce((s, c) => s + c.total, 0);
    if (total === 0) return [];
    let startAngle = 0;
    return expCats.map((c) => {
      const angle = (c.total / total) * 360;
      const item = { ...c, startAngle, angle, percent: ((c.total / total) * 100).toFixed(1) };
      startAngle += angle;
      return item;
    });
  }, [summary]);

  const describeArc = (cx: number, cy: number, r: number, start: number, end: number): string => {
    const rad = (a: number) => ((a - 90) * Math.PI) / 180;
    const sx = cx + r * Math.cos(rad(start));
    const sy = cy + r * Math.sin(rad(start));
    const ex = cx + r * Math.cos(rad(end));
    const ey = cy + r * Math.sin(rad(end));
    const large = end - start > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey} Z`;
  };

  if (!loaded) return <BudgetSkeleton />;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white">Бюджет</h2>
          {/* Tabs */}
          <div className="flex bg-slate-800 rounded-xl p-0.5">
            {([['overview', 'Обзор'], ['cards', 'Счета'], ['categories', 'Категории'], ['planned', 'Подписки'], ['credits', 'Кредиты']] as [Tab, string][]).map(([t, label]) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3.5 py-1.5 text-sm rounded-lg transition-colors font-medium relative ${tab === t ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {label}
                {t === 'planned' && duePlannedCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center font-bold">{duePlannedCount}</span>
                )}
                {t === 'credits' && credits.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-[10px] text-white flex items-center justify-center font-bold">{credits.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick period */}
          <div className="flex bg-slate-800 rounded-xl p-0.5 gap-0.5">
            {([['today', 'Д', 'День'], ['week', 'Н', 'Неделя'], ['month', 'М', 'Месяц'], ['year', 'Г', 'Год']] as [QuickPeriod, string, string][]).map(([p, label, title]) => (
              <button
                key={p}
                onClick={() => applyQuickPeriod(p)}
                className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors ${quickPeriod === p ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                title={title}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setQuickPeriod('custom'); }}
              className="bg-slate-800 border border-slate-600 rounded-xl px-2.5 py-1.5 text-sm w-34 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            <span className="text-slate-600">—</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setQuickPeriod('custom'); }}
              className="bg-slate-800 border border-slate-600 rounded-xl px-2.5 py-1.5 text-sm w-34 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
          </div>
          <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors font-medium">
            <Plus size={18} /> Запись
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ===== OVERVIEW TAB ===== */}
        {tab === 'overview' && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3 px-4 py-4">
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-green-400 mb-1">
                  <TrendingUp size={16} />
                  <span className="text-sm">Доход</span>
                </div>
                <p className="text-2xl font-bold text-green-300">+{fmt(totalIncome)} ₽</p>
              </div>
              <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-red-400 mb-1">
                  <TrendingDown size={16} />
                  <span className="text-sm">Расход</span>
                </div>
                <p className="text-2xl font-bold text-red-300">−{fmt(totalExpense)} ₽</p>
              </div>
              <div className={`bg-gradient-to-br ${balance >= 0 ? 'from-indigo-500/10 to-indigo-600/5 border-indigo-500/20' : 'from-orange-500/10 to-orange-600/5 border-orange-500/20'} border rounded-2xl p-4`}>
                <div className={`flex items-center gap-2 mb-1 ${balance >= 0 ? 'text-indigo-400' : 'text-orange-400'}`}>
                  <Wallet size={16} />
                  <span className="text-sm">Общий баланс</span>
                </div>
                <p className={`text-2xl font-bold ${balance >= 0 ? 'text-indigo-300' : 'text-orange-300'}`}>
                  {balance >= 0 ? '+' : ''}{fmt(balance)} ₽
                </p>
                {totalCreditDebt > 0 && (
                  <p className="text-xs text-amber-400/80 mt-1 flex items-center gap-1">
                    <Landmark size={10} />
                    Долг: −{fmt(totalCreditDebt)} ₽
                  </p>
                )}
                {creditBalance !== 0 && (
                  <p className="text-xs text-slate-400/60 mt-0.5">
                    Кредитки: {fmt(creditBalance)} ₽
                  </p>
                )}
              </div>
            </div>

            {/* Quick stats */}
            {entries.length > 0 && (
              <div className="grid grid-cols-3 gap-3 px-4 pb-4">
                <div className="bg-gradient-to-br from-orange-500/10 to-amber-600/5 border border-orange-500/20 rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-orange-400 mb-2">
                    <TrendingDown size={18} />
                    <span className="text-sm font-medium">Средний расход/день</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-300">{fmt(avgExpensePerDay)} ₽</p>
                  <p className="text-xs text-slate-500 mt-1">за {daysInPeriod} дней</p>
                </div>
                {topCategory ? (
                  <div className="bg-gradient-to-br from-purple-500/10 to-violet-600/5 border border-purple-500/20 rounded-2xl p-4">
                    <div className="flex items-center gap-2 text-purple-400 mb-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: topCategory.color || '#6366f1' }} />
                      <span className="text-sm font-medium">Топ категория</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-300">{fmt(topCategory.total)} ₽</p>
                    <p className="text-xs text-slate-500 mt-1">{topCategory.name}</p>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-slate-500/10 to-slate-600/5 border border-slate-500/20 rounded-2xl p-4">
                    <div className="flex items-center gap-2 text-slate-400 mb-2">
                      <Tag size={18} />
                      <span className="text-sm font-medium">Топ категория</span>
                    </div>
                    <p className="text-lg font-bold text-slate-500">—</p>
                  </div>
                )}
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/5 border border-cyan-500/20 rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-cyan-400 mb-2">
                    <Calendar size={18} />
                    <span className="text-sm font-medium">Записей</span>
                  </div>
                  <p className="text-2xl font-bold text-cyan-300">{entries.length}</p>
                  <p className="text-xs text-slate-500 mt-1">за {daysInPeriod} дней</p>
                </div>
              </div>
            )}

            {/* Cards horizontal scroll */}
            {cards.length > 0 && (
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard size={14} className="text-slate-500" />
                  <span className="text-sm text-slate-400">Счета</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
                  {cards.map((card) => {
                    const cardSummary = summary?.byCard?.find(b => b.card_id === card.id);
                    return (
                      <div
                        key={card.id}
                        className="shrink-0 w-56 h-32 rounded-2xl p-4 flex flex-col justify-between text-white shadow-lg relative overflow-hidden"
                        style={{ background: card.color || CARD_GRADIENT_PRESETS[0] }}
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-white/10 -translate-y-8 translate-x-8" />
                        <div className="absolute bottom-0 left-0 w-16 h-16 rounded-full bg-white/5 translate-y-6 -translate-x-6" />
                        <div className="flex items-start justify-between relative">
                          <div>
                            <p className="text-xs opacity-70">{card.bank || 'Карта'}</p>
                            <p className="font-semibold text-sm mt-0.5">{card.name}</p>
                          </div>
                          <CreditCard size={20} className="opacity-40" />
                        </div>
                        <div className="relative">
                          <p className="text-xs opacity-60 mb-0.5">•••• {card.last4 || '????'}</p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold">{fmt(card.balance)} {currencySymbol(card.currency)}</span>
                            {cardSummary && (
                              <span className="text-[10px] opacity-70">
                                {cardSummary.total_income > 0 && `+${fmt(cardSummary.total_income)}`}
                                {cardSummary.total_expense > 0 && ` −${fmt(cardSummary.total_expense)}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pie chart + category breakdown */}
            {pieData.length > 0 && (
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag size={14} className="text-slate-500" />
                  <span className="text-sm text-slate-400">Расходы по категориям</span>
                </div>
                <div className="flex gap-6 items-start">
                  <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0">
                    {pieData.map((d, i) => (
                      <path
                        key={i}
                        d={describeArc(50, 50, 45, d.startAngle, d.startAngle + d.angle - 0.5)}
                        fill={d.color || '#6366f1'}
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                      >
                        <title>{d.name}: {fmt(d.total)} ₽ ({d.percent}%)</title>
                      </path>
                    ))}
                    <circle cx="50" cy="50" r="22" fill="var(--color-slate-900, #0f172a)" />
                    <text x="50" y="48" textAnchor="middle" className="fill-slate-400 text-[6px]">Всего</text>
                    <text x="50" y="57" textAnchor="middle" className="fill-white text-[8px] font-bold">{fmt(totalExpense)}</text>
                  </svg>
                  <div className="flex-1 grid grid-cols-2 gap-1.5">
                    {pieData.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-800/30 rounded-lg px-2.5 py-1.5">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color || '#6366f1' }} />
                        <span className="text-xs text-slate-300 flex-1 truncate">{d.name || 'Без категории'}</span>
                        <span className="text-xs text-red-400 font-medium">{fmt(d.total)} ₽</span>
                        <span className="text-[10px] text-slate-600">{d.percent}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Daily income/expense line chart */}
            {dailyChartData.length >= 2 && (() => {
              const W = 600, H = 140, PX = 40, PY = 16;
              const maxVal = Math.max(...dailyChartData.flatMap(d => [d.income, d.expense]), 1);
              const toX = (i: number) => PX + i * (W - PX * 2) / (dailyChartData.length - 1);
              const toY = (v: number) => PY + (H - PY * 2) * (1 - v / maxVal);
              const incomePts = dailyChartData.map((d, i) => `${toX(i)},${toY(d.income)}`).join(' ');
              const expensePts = dailyChartData.map((d, i) => `${toX(i)},${toY(d.expense)}`).join(' ');
              const incomeArea = `M ${toX(0)},${toY(dailyChartData[0].income)} L ${incomePts} L ${toX(dailyChartData.length - 1)},${H - PY} L ${toX(0)},${H - PY} Z`;
              const expenseArea = `M ${toX(0)},${toY(dailyChartData[0].expense)} L ${expensePts} L ${toX(dailyChartData.length - 1)},${H - PY} L ${toX(0)},${H - PY} Z`;
              const ticks = dailyChartData.length <= 10 ? dailyChartData.map((d, i) => ({ i, label: d.date.slice(5) })) :
                [0, Math.floor(dailyChartData.length / 2), dailyChartData.length - 1].map(i => ({ i, label: dailyChartData[i].date.slice(5) }));
              return (
                <div className="px-4 pb-4">
                  <div className="bg-slate-800/30 rounded-2xl p-4 border border-slate-700/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-300">Доходы и расходы по дням</span>
                      <div className="flex gap-4 text-xs">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded" />Доходы</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-red-400 inline-block rounded" />Расходы</span>
                      </div>
                    </div>
                    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 120 }}>
                      <defs>
                        <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#34d399" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f87171" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {/* Grid lines */}
                      {[0, 0.25, 0.5, 0.75, 1].map(t => (
                        <line key={t} x1={PX} y1={PY + (H - PY * 2) * t} x2={W - PX} y2={PY + (H - PY * 2) * t}
                          stroke="#334155" strokeWidth="0.5" strokeDasharray="4,4" />
                      ))}
                      {/* Areas */}
                      <path d={incomeArea} fill="url(#incGrad)" />
                      <path d={expenseArea} fill="url(#expGrad)" />
                      {/* Lines */}
                      <polyline points={incomePts} fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinejoin="round" />
                      <polyline points={expensePts} fill="none" stroke="#f87171" strokeWidth="1.5" strokeLinejoin="round" />
                      {/* X-axis labels */}
                      {ticks.map(({ i, label }) => (
                        <text key={i} x={toX(i)} y={H} textAnchor="middle" className="fill-slate-500" fontSize="8">{label}</text>
                      ))}
                      {/* Y-axis labels */}
                      {[0, 0.5, 1].map(t => (
                        <text key={t} x={PX - 4} y={PY + (H - PY * 2) * (1 - t) + 3} textAnchor="end" className="fill-slate-500" fontSize="7">
                          {t === 0 ? '0' : Math.round(maxVal * t / 1000) >= 1 ? `${Math.round(maxVal * t / 1000)}k` : Math.round(maxVal * t)}
                        </text>
                      ))}
                    </svg>
                  </div>
                </div>
              );
            })()}

            {/* Add form */}
            {showAdd && <EntryForm
              categories={categories} cards={cards} noteOptions={noteOptions}
              newAmount={newAmount} setNewAmount={setNewAmount}
              newType={newType} setNewType={setNewType}
              newCat={newCat} setNewCat={setNewCat}
              newCard={newCard} setNewCard={setNewCard}
              newDesc={newDesc} setNewDesc={setNewDesc}
              newDate={newDate} setNewDate={setNewDate}
              newNoteId={newNoteId} setNewNoteId={setNewNoteId}
              newRecurring={newRecurring} setNewRecurring={setNewRecurring}
              onSubmit={addEntry} onCancel={() => setShowAdd(false)}
            />}

            {/* Entry list — at the bottom */}
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500">Записи ({filteredEntries.length})</span>
                <div className="flex items-center gap-2">
                  {filteredEntries.length > 0 && (
                    <button onClick={exportCSV} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-700/60 hover:bg-slate-600/60 text-xs text-slate-300 transition-colors">
                      <FileText size={12} />CSV
                    </button>
                  )}
                  {cards.length > 0 && (
                    <select value={filterCard} onChange={(e) => setFilterCard(e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs focus:outline-none text-slate-300">
                      <option value="">Все карты</option>
                      {cards.map(c => (
                        <option key={c.id} value={c.id}>{c.name} •{c.last4}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                {filteredEntries.map((e) => (
                  <div key={e.id} className={`flex items-center gap-3 bg-slate-800/30 rounded-xl px-3 py-2.5 group hover:bg-slate-800/50 transition-colors ${e.is_excluded ? 'opacity-40' : ''}`}>
                    {e.card_name && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md text-white/80 shrink-0" style={{ background: e.card_color || '#6366f1' }}>
                        {e.card_name} •{e.card_last4}
                      </span>
                    )}
                    <div className={`text-sm font-medium w-28 text-right shrink-0 ${e.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                      {e.type === 'income' ? '+' : '−'}{fmt(e.amount)} {currencySymbol(cards.find(c => c.id === e.card_id)?.currency)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {e.category_name && (
                          <span className="flex items-center gap-1 text-xs">
                            <span className="w-2 h-2 rounded-full" style={{ background: e.category_color || '#6366f1' }} />
                            <span className="text-slate-400">{e.category_name}</span>
                          </span>
                        )}
                        {e.note_title && (
                          <button
                            onClick={() => e.note_id && onNavigateToNote?.(e.note_id)}
                            className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-colors"
                          >
                            <FileText size={10} />
                            {e.note_title}
                          </button>
                        )}
                        {editingEntryId === e.id ? (
                          <input
                            defaultValue={e.description}
                            onBlur={(ev) => updateEntry(e.id, { description: ev.target.value })}
                            onKeyDown={(ev) => ev.key === 'Enter' && updateEntry(e.id, { description: (ev.target as HTMLInputElement).value })}
                            className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-0.5 text-xs focus:outline-none"
                            autoFocus
                          />
                        ) : (
                          e.description && (
                            <span
                              className="text-xs text-slate-500 truncate cursor-pointer hover:text-slate-400"
                              onClick={() => setEditingEntryId(e.id)}
                            >
                              {e.description}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-slate-600 shrink-0">
                      {e.recurring && e.recurring !== 'none' && <span className="mr-1" title={`Повтор: ${e.recurring === 'daily' ? 'ежедневно' : e.recurring === 'weekly' ? 'еженедельно' : 'ежемесячно'}`}>🔄</span>}
                      {new Date(e.date).toLocaleDateString('ru-RU')}
                    </span>
                    <button
                      onClick={() => updateEntry(e.id, { is_excluded: e.is_excluded ? 0 : 1 })}
                      className={`shrink-0 transition-all ${e.is_excluded ? 'text-yellow-500 opacity-100' : 'text-slate-600 hover:text-yellow-400 opacity-0 group-hover:opacity-100'}`}
                      title={e.is_excluded ? 'Учитывать в бюджете' : 'Не учитывать в бюджете'}
                    >
                      {e.is_excluded ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => deleteEntry(e.id)} className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {filteredEntries.length === 0 && (
                  <p className="text-center text-slate-500 text-sm py-8">Нет записей за выбранный период</p>
                )}
              </div>
            </div>

          </>
        )}

        {/* ===== CARDS TAB ===== */}
        {tab === 'cards' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-300">Счета и кошельки</h3>
              <div className="flex gap-2">
                {cards.length >= 2 && (
                  <button
                    onClick={() => setShowTransfer(!showTransfer)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-xl transition-colors"
                  >
                    <ArrowRightLeft size={14} /> Перевод
                  </button>
                )}
                <button
                  onClick={() => { resetCardForm(); setShowCardForm(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors"
                >
                  <Plus size={14} /> Добавить счёт
                </button>
              </div>
            </div>

            {/* Transfer form */}
            {showTransfer && (
              <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-2xl p-4 space-y-3 animate-slide-down">
                <p className="text-sm font-medium text-emerald-300 flex items-center gap-2">
                  <ArrowRightLeft size={16} /> Перевод между картами
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Откуда</label>
                    <select value={transferFrom} onChange={(e) => setTransferFrom(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50">
                      <option value="">Выберите карту</option>
                      {cards.filter(c => String(c.id) !== transferTo).map(c => <option key={c.id} value={c.id}>{c.name} •{c.last4} ({fmt(c.balance)} {currencySymbol(c.currency)})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Куда</label>
                    <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50">
                      <option value="">Выберите карту</option>
                      {cards.filter(c => String(c.id) !== transferFrom).map(c => <option key={c.id} value={c.id}>{c.name} •{c.last4} ({fmt(c.balance)} {currencySymbol(c.currency)})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Сумма</label>
                    <input type="number" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      onKeyDown={(e) => e.key === 'Enter' && doTransfer()} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={doTransfer} className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-xl transition-colors">
                    Перевести
                  </button>
                  <button onClick={() => { setShowTransfer(false); setTransferFrom(''); setTransferTo(''); setTransferAmount(''); }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            {/* Card form */}
            {showCardForm && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3 animate-slide-down">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Название</label>
                    <input value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder="Моя карта"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" autoFocus />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Банк</label>
                    <input value={cardBank} onChange={(e) => setCardBank(e.target.value)} placeholder="Сбербанк"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Последние 4 цифры</label>
                    <input value={cardLast4} onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="1234" maxLength={4}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Баланс</label>
                    <input type="number" value={cardBalance} onChange={(e) => setCardBalance(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Цвет карты</label>
                  <div className="flex gap-2 flex-wrap">
                    {CARD_GRADIENT_PRESETS.map((g, i) => (
                      <button
                        key={i}
                        onClick={() => setCardColor(g)}
                        className={`w-8 h-8 rounded-xl transition-transform ${cardColor === g ? 'ring-2 ring-white scale-110' : 'hover:scale-105'}`}
                        style={{ background: g }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={cardIsCredit} onChange={(e) => setCardIsCredit(e.target.checked)} className="rounded border-slate-600" />
                    <span className="text-sm text-slate-300">Кредитная карта</span>
                    <span className="text-xs text-slate-500">(долг не учитывается в балансе)</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Валюта</label>
                  <div className="flex gap-2">
                    {(['RUB', 'USD', 'EUR'] as const).map(cur => (
                      <button key={cur} onClick={() => setCardCurrency(cur)}
                        className={`px-3 py-1.5 rounded-xl text-sm transition-colors ${cardCurrency === cur ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        {cur === 'RUB' ? '₽ RUB' : cur === 'USD' ? '$ USD' : '€ EUR'}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Preview card */}
                <div
                  className="w-full h-28 rounded-2xl p-4 flex flex-col justify-between text-white shadow-lg relative overflow-hidden"
                  style={{ background: cardColor }}
                >
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-white/10 -translate-y-8 translate-x-8" />
                  <div className="flex items-start justify-between relative">
                    <div>
                      <p className="text-xs opacity-70">{cardBank || 'Банк'}</p>
                      <p className="font-semibold text-sm mt-0.5">{cardName || 'Название'}</p>
                    </div>
                    <CreditCard size={20} className="opacity-40" />
                  </div>
                  <div className="relative">
                    <p className="text-xs opacity-60">•••• {cardLast4 || '????'}</p>
                    <p className="text-lg font-bold">{fmt(parseFloat(cardBalance) || 0)} {currencySymbol(cardCurrency)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCard} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                    {editingCardId ? 'Сохранить' : 'Создать карту'}
                  </button>
                  <button onClick={resetCardForm} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            {/* Cards grid */}
            <div className="grid grid-cols-2 gap-4">
              {cards.map((card) => {
                const cardSummary = summary?.byCard?.find(b => b.card_id === card.id);
                return (
                  <div key={card.id} className="group relative">
                    <div
                      className="w-full h-36 rounded-2xl p-4 flex flex-col justify-between text-white shadow-lg relative overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
                      style={{ background: card.color || CARD_GRADIENT_PRESETS[0] }}
                    >
                      <div className="absolute top-0 right-0 w-28 h-28 rounded-full bg-white/10 -translate-y-10 translate-x-10" />
                      <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full bg-white/5 translate-y-8 -translate-x-8" />
                      <div className="flex items-start justify-between relative">
                        <div>
                          <p className="text-xs opacity-70">{card.bank || 'Карта'}</p>
                          <p className="font-semibold">{card.name}</p>
                        </div>
                        <CreditCard size={22} className="opacity-30" />
                      </div>
                      <div className="relative">
                        <p className="text-sm opacity-60 mb-0.5">•••• {card.last4 || '????'}</p>
                        <p className="text-xl font-bold">{fmt(card.balance)} {currencySymbol(card.currency)}</p>
                        {cardSummary && (
                          <div className="flex gap-2 mt-1 text-[10px]">
                            {cardSummary.total_income > 0 && (
                              <span className="bg-white/20 rounded-md px-1.5 py-0.5">+{fmt(cardSummary.total_income)}</span>
                            )}
                            {cardSummary.total_expense > 0 && (
                              <span className="bg-black/20 rounded-md px-1.5 py-0.5">−{fmt(cardSummary.total_expense)}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Actions overlay */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => editCard(card)} className="p-1.5 rounded-lg bg-black/30 hover:bg-black/50 text-white/80 backdrop-blur-sm">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => deleteCard(card.id)} className="p-1.5 rounded-lg bg-black/30 hover:bg-red-500/50 text-white/80 backdrop-blur-sm">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {cards.length === 0 && !showCardForm && (
              <div className="text-center py-12">
                <CreditCard size={40} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400 text-sm mb-2">Нет добавленных карт</p>
                <p className="text-slate-500 text-xs mb-4">Добавьте карту, чтобы распределять бюджет</p>
                <button
                  onClick={() => setShowCardForm(true)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors"
                >
                  Добавить карту
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== CATEGORIES TAB ===== */}
        {tab === 'categories' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-300">Категории</h3>
              <button
                onClick={() => { setCatName(''); setCatColor('#6366f1'); setCatIcon(''); setEditingCatId(null); setShowCatForm(!showCatForm); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors"
              >
                <Plus size={14} /> Добавить
              </button>
            </div>

            {showCatForm && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3 animate-slide-down">
                <p className="text-sm font-medium text-slate-300">{editingCatId ? 'Редактировать категорию' : 'Новая категория'}</p>
                <div className="flex gap-3">
                  <input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Название"
                    className="flex-1 bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" autoFocus />
                  <input type="color" value={catColor} onChange={(e) => setCatColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-0 shrink-0" />
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-lg border" style={{ borderColor: `${catColor}60`, background: `${catColor}20` }}>
                    {catIcon || <span className="text-slate-600 text-xs">?</span>}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">Иконка</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORY_ICONS.map((ic) => (
                      <button key={ic} onClick={() => setCatIcon(ic)}
                        className={`text-lg w-9 h-9 flex items-center justify-center rounded-lg transition-all ${catIcon === ic ? 'ring-2 ring-indigo-500 bg-indigo-500/20' : 'bg-slate-800 hover:bg-slate-700'}`}>
                        {ic}
                      </button>
                    ))}
                    <button onClick={() => setCatIcon('')}
                      className={`text-xs px-2 h-9 rounded-lg transition-all ${catIcon === '' ? 'ring-2 ring-slate-500 bg-slate-700 text-slate-300' : 'bg-slate-800 hover:bg-slate-700 text-slate-500'}`}>
                      нет
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={addCategory} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                    {editingCatId ? 'Сохранить' : 'Создать'}
                  </button>
                  <button onClick={() => { setCatName(''); setCatColor('#6366f1'); setCatIcon(''); setShowCatForm(false); setEditingCatId(null); }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {categories.map((c) => {
                const total = summary?.byCategory.find(b => b.name === c.name)?.total;
                return (
                  <div key={c.id} className="flex items-center gap-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl px-3 py-2.5 group hover:border-slate-600 transition-colors">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${c.color}20`, border: `1px solid ${c.color}40` }}>
                      {c.icon ? (
                        <span className="text-base">{c.icon}</span>
                      ) : (
                        <div className="w-3 h-3 rounded-full" style={{ background: c.color }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-300 truncate">{c.name}</p>
                      {total !== undefined && <p className="text-xs text-slate-500">{fmt(total)} ₽</p>}
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                      <button onClick={() => editCategory(c)} className="p-1 text-slate-600 hover:text-indigo-400 rounded-lg">
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => deleteCategory(c.id)} className="p-1 text-slate-600 hover:text-red-400 rounded-lg">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {categories.length === 0 && !showCatForm && (
              <div className="text-center py-12">
                <Tag size={40} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400 text-sm">Нет категорий</p>
              </div>
            )}

            {/* Budget goals / limits per category */}
            {categories.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Месячные лимиты</h4>
                <div className="space-y-2">
                  {categories.map((c) => {
                    const goal = goals.find(g => g.category_id === c.id);
                    const catExpense = summary?.byCategory.find(b => b.name === c.name)?.total ?? 0;
                    const limit = goal?.monthly_limit ?? 0;
                    const progress = limit > 0 ? Math.min(catExpense / limit, 1) : 0;
                    const over80 = limit > 0 && catExpense >= limit * 0.8;
                    const over100 = limit > 0 && catExpense >= limit;
                    return (
                      <div key={c.id} className="bg-slate-800/30 border border-slate-700/30 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-sm"
                            style={{ background: `${c.color}20`, border: `1px solid ${c.color}40` }}>
                            {c.icon || <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />}
                          </div>
                          <span className="flex-1 text-sm text-slate-300 truncate">{c.name}</span>
                          <span className="text-xs text-slate-500 mr-1">{fmt(catExpense)} ₽</span>
                          <input
                            type="number" min="0" placeholder="Лимит"
                            value={goalInputs[c.id] ?? ''}
                            onChange={(e) => setGoalInputs(prev => ({ ...prev, [c.id]: e.target.value }))}
                            className="w-24 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500/50 text-slate-300 text-right"
                          />
                          <button onClick={() => saveGoal(c.id)}
                            className="p-1 text-slate-500 hover:text-indigo-400 transition-colors" title="Сохранить лимит">
                            <Check size={14} />
                          </button>
                          {goal && (
                            <button onClick={() => deleteGoal(c.id)}
                              className="p-1 text-slate-600 hover:text-red-400 transition-colors" title="Удалить лимит">
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        {limit > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${over100 ? 'bg-red-500' : over80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{ width: `${progress * 100}%` }}
                              />
                            </div>
                            <div className="flex justify-between mt-0.5">
                              <span className={`text-[10px] ${over100 ? 'text-red-400' : over80 ? 'text-amber-400' : 'text-slate-500'}`}>
                                {over100 ? 'Превышен лимит!' : over80 ? `${Math.round(progress * 100)}% лимита` : `${Math.round(progress * 100)}%`}
                              </span>
                              <span className="text-[10px] text-slate-600">из {fmt(limit)} ₽</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== PLANNED TAB ===== */}
        {tab === 'planned' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-300">Планируемые расходы / Подписки</h3>
              <button
                onClick={() => { resetPlannedForm(); setShowPlannedForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors"
              >
                <Plus size={14} /> Добавить
              </button>
            </div>

            {showPlannedForm && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3 animate-slide-down">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Название</label>
                    <input value={plannedTitle} onChange={(e) => setPlannedTitle(e.target.value)} placeholder="Netflix, аренда..."
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" autoFocus />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Сумма</label>
                    <input type="number" value={plannedAmount} onChange={(e) => setPlannedAmount(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Категория</label>
                    <select value={plannedCat} onChange={(e) => setPlannedCat(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
                      <option value="">—</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Карта</label>
                    <select value={plannedCard} onChange={(e) => setPlannedCard(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
                      <option value="">—</option>
                      {cards.map(c => <option key={c.id} value={c.id}>{c.name} •{c.last4}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">День месяца</label>
                    <input type="number" min={1} max={31} value={plannedDay} onChange={(e) => setPlannedDay(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer py-1">
                    <input type="checkbox" checked={plannedRecurring} onChange={(e) => setPlannedRecurring(e.target.checked)}
                      className="rounded border-slate-600" />
                    <span className="text-sm text-slate-300">Повторять</span>
                  </label>
                  {plannedRecurring && (
                    <div className="flex gap-1.5">
                      {([['monthly', 'Месяц', '1'], ['half_year', 'Полгода', '6'], ['year', 'Год', '12']] as const).map(([type, label, months]) => (
                        <button key={type} onClick={() => { setPlannedPeriodType(type); setPlannedPeriodMonths(months); }}
                          className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${plannedPeriodType === type ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                          {label}
                        </button>
                      ))}
                      <button onClick={() => setPlannedPeriodType('custom')}
                        className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${plannedPeriodType === 'custom' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        Свой
                      </button>
                      {plannedPeriodType === 'custom' && (
                        <input type="number" min={1} max={120} value={plannedPeriodMonths} onChange={(e) => setPlannedPeriodMonths(e.target.value)}
                          className="w-16 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs text-center focus:outline-none" placeholder="мес." />
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Заметка</label>
                  <input value={plannedNote} onChange={(e) => setPlannedNote(e.target.value)} placeholder="Описание..."
                    className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && savePlanned()} />
                </div>
                <div className="flex gap-2">
                  <button onClick={savePlanned} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                    {editingPlannedId ? 'Сохранить' : 'Создать'}
                  </button>
                  <button onClick={resetPlannedForm} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            {/* Planned list */}
            <div className="space-y-2">
              {planned.map((p) => {
                const isDue = p.next_due <= new Date().toISOString().slice(0, 10);
                return (
                  <div key={p.id} className={`flex items-center gap-3 rounded-2xl px-4 py-3 group transition-colors border ${isDue ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-white">{p.title}</span>
                        {p.is_recurring ? (
                          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300">
                            <RepeatIcon size={9} /> {p.period_type === 'year' || p.period_months === 12 ? 'ежегодно' : p.period_type === 'half_year' || p.period_months === 6 ? 'раз в полгода' : p.period_months > 1 ? `раз в ${p.period_months} мес.` : 'ежемесячно'}
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-700 text-slate-400">разовый</span>
                        )}
                        {isDue && (
                          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-red-500/20 text-red-300 animate-pulse">
                            <Bell size={9} /> к оплате!
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {p.category_name && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ background: p.category_color || '#6366f1' }} />
                            {p.category_name}
                          </span>
                        )}
                        {p.card_name && (
                          <span className="px-1.5 py-0.5 rounded-md text-[10px] text-white/70" style={{ background: p.card_color || '#6366f1' }}>
                            {p.card_name} •{p.card_last4}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock size={10} />
                          {p.day_of_month}-е число
                        </span>
                        <span>→ {new Date(p.next_due).toLocaleDateString('ru-RU')}</span>
                        {p.last_paid && <span className="text-green-400/60">Посл. оплата: {new Date(p.last_paid).toLocaleDateString('ru-RU')}</span>}
                      </div>
                      {p.note && <p className="text-xs text-slate-600 mt-1">{p.note}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-red-400">−{fmt(p.amount)} {currencySymbol(cards.find(c => c.id === p.card_id)?.currency)}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => markPaid(p.id)} title="Оплачено"
                        className={`p-2 rounded-xl transition-colors ${isDue ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'text-slate-600 hover:text-green-400 hover:bg-green-500/10'}`}>
                        <Check size={16} />
                      </button>
                      <button onClick={() => editPlanned(p)} className="p-2 text-slate-600 hover:text-white rounded-xl opacity-0 group-hover:opacity-100 transition-all">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => deletePlanned(p.id)} className="p-2 text-slate-600 hover:text-red-400 rounded-xl opacity-0 group-hover:opacity-100 transition-all">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {planned.length === 0 && !showPlannedForm && (
                <div className="text-center py-12">
                  <Bell size={40} className="mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400 text-sm mb-2">Нет запланированных расходов</p>
                  <p className="text-slate-500 text-xs mb-4">Добавьте подписки и регулярные платежи</p>
                  <button onClick={() => setShowPlannedForm(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                    Добавить подписку
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== CREDITS TAB ===== */}
        {tab === 'credits' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-300">Кредиты и займы</h3>
                {totalCreditDebt > 0 && (
                  <p className="text-xs text-amber-400 mt-0.5">Общий долг: {fmt(totalCreditDebt)} ₽</p>
                )}
              </div>
              <button
                onClick={() => { resetCreditForm(); setShowCreditForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-xl transition-colors"
              >
                <Plus size={14} /> Добавить кредит
              </button>
            </div>

            {/* Credit form */}
            {showCreditForm && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3 animate-slide-down">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Название</label>
                    <input value={creditTitle} onChange={(e) => setCreditTitle(e.target.value)} placeholder="Ипотека, Автокредит..."
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" autoFocus />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Общая сумма кредита ₽</label>
                    <input type="number" value={creditTotal} onChange={(e) => setCreditTotal(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Уже оплачено ₽</label>
                    <input type="number" value={creditPaid} onChange={(e) => setCreditPaid(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Ежемес. взнос ₽</label>
                    <input type="number" value={creditMonthly} onChange={(e) => setCreditMonthly(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Ставка % (год)</label>
                    <input type="number" step="0.1" value={creditRate} onChange={(e) => setCreditRate(e.target.value)} placeholder="0"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Дата погашения</label>
                    <input type="date" value={creditEndDate} onChange={(e) => setCreditEndDate(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Карта</label>
                    <select value={creditCard} onChange={(e) => setCreditCard(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
                      <option value="">—</option>
                      {cards.map(c => <option key={c.id} value={c.id}>{c.name} •{c.last4}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">День оплаты (число месяца)</label>
                    <input type="number" min={1} max={31} value={creditDayOfMonth} onChange={(e) => setCreditDayOfMonth(e.target.value)} placeholder="Напр. 15"
                      className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                  </div>
                  <div className="flex items-end pb-1">
                    <p className="text-xs text-slate-500">Уведомление в день оплаты</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Заметка</label>
                  <input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Дополнительная информация..."
                    className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCredit} className="flex-1 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-xl transition-colors">
                    {editingCreditId ? 'Сохранить' : 'Добавить кредит'}
                  </button>
                  <button onClick={resetCreditForm} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            {/* Credits list */}
            <div className="space-y-3">
              {credits.map((c) => {
                const remaining = Math.max(0, c.total_amount - c.paid_amount);
                const progress = c.total_amount > 0 ? Math.min(100, (c.paid_amount / c.total_amount) * 100) : 0;
                const monthsLeft = c.monthly_payment > 0 ? Math.ceil(remaining / c.monthly_payment) : null;
                const isDueToday = c.day_of_month !== null && c.day_of_month === new Date().getDate();
                return (
                  <div key={c.id} className={`bg-slate-800/40 border rounded-2xl p-4 group ${isDueToday ? 'border-red-500/40' : 'border-amber-500/20'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-white">{c.title}</span>
                          {c.interest_rate !== null && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300">{c.interest_rate}%/год</span>
                          )}
                          {c.card_name && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md text-white/70" style={{ background: c.card_color || '#6366f1' }}>
                              {c.card_name} •{c.card_last4}
                            </span>
                          )}
                          {c.day_of_month !== null && (
                            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-slate-700 text-slate-400">
                              <Clock size={9} /> {c.day_of_month}-е число
                            </span>
                          )}
                          {isDueToday && (
                            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-red-500/20 text-red-300 animate-pulse">
                              <Bell size={9} /> к оплате!
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {c.end_date && <span className="flex items-center gap-1"><Calendar size={10} />до {new Date(c.end_date).toLocaleDateString('ru-RU')}</span>}
                          {monthsLeft !== null && <span>~{monthsLeft} мес. осталось</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => payCreditInstallment(c.id)} title="Внести платёж"
                          className="flex items-center gap-1 px-2 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-xl text-xs transition-colors">
                          <Check size={12} /> Взнос
                        </button>
                        <button onClick={() => editCredit(c)} className="p-1.5 text-slate-600 hover:text-white rounded-xl opacity-0 group-hover:opacity-100 transition-all">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => deleteCredit(c.id)} className="p-1.5 text-slate-600 hover:text-red-400 rounded-xl opacity-0 group-hover:opacity-100 transition-all">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-green-400">Оплачено: {fmt(c.paid_amount)} ₽</span>
                        <span className="text-amber-400">Остаток: {fmt(remaining)} ₽</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] mt-1 text-slate-600">
                        <span>{progress.toFixed(1)}% погашено</span>
                        <span>Итого: {fmt(c.total_amount)} ₽</span>
                      </div>
                    </div>

                    {c.monthly_payment > 0 && (
                      <p className="text-xs text-slate-500">Ежемесячный взнос: <span className="text-slate-300">{fmt(c.monthly_payment)} ₽</span></p>
                    )}
                    {c.note && <p className="text-xs text-slate-600 mt-1">{c.note}</p>}
                  </div>
                );
              })}
              {credits.length === 0 && !showCreditForm && (
                <div className="text-center py-12">
                  <Landmark size={40} className="mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400 text-sm mb-2">Нет активных кредитов</p>
                  <p className="text-slate-500 text-xs mb-4">Добавьте кредиты для отслеживания погашения</p>
                  <button onClick={() => setShowCreditForm(true)}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-xl transition-colors">
                    Добавить кредит
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- Entry form sub-component ---- */
function EntryForm({ categories, cards, noteOptions, newAmount, setNewAmount, newType, setNewType, newCat, setNewCat, newCard, setNewCard, newDesc, setNewDesc, newDate, setNewDate, newNoteId, setNewNoteId, newRecurring, setNewRecurring, onSubmit, onCancel }: {
  categories: BudgetCategory[];
  cards: BudgetCard[];
  noteOptions: NoteOption[];
  newAmount: string; setNewAmount: (v: string) => void;
  newType: 'income' | 'expense'; setNewType: (v: 'income' | 'expense') => void;
  newCat: string; setNewCat: (v: string) => void;
  newCard: string; setNewCard: (v: string) => void;
  newDesc: string; setNewDesc: (v: string) => void;
  newDate: string; setNewDate: (v: string) => void;
  newNoteId: string; setNewNoteId: (v: string) => void;
  newRecurring: string; setNewRecurring: (v: string) => void;
  onSubmit: () => void; onCancel: () => void;
}) {
  return (
    <div className="px-4 pb-4 animate-slide-down">
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-300">Новая запись</span>
          <button onClick={onCancel} className="text-slate-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Сумма</label>
            <input type="number" step="0.01" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="0.00"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" autoFocus />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Тип</label>
            <div className="flex gap-2">
              <button onClick={() => setNewType('expense')}
                className={`flex-1 py-2 text-sm rounded-xl border transition-colors ${newType === 'expense' ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'border-slate-600 text-slate-400'}`}>
                Расход
              </button>
              <button onClick={() => setNewType('income')}
                className={`flex-1 py-2 text-sm rounded-xl border transition-colors ${newType === 'income' ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'border-slate-600 text-slate-400'}`}>
                Доход
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Категория</label>
            <select value={newCat} onChange={(e) => setNewCat(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
              <option value="">—</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Карта</label>
            <select value={newCard} onChange={(e) => setNewCard(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
              <option value="">—</option>
              {cards.map(c => (
                <option key={c.id} value={c.id}>{c.name} •{c.last4}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Дата</label>
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Описание</label>
            <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Описание"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              onKeyDown={(e) => e.key === 'Enter' && onSubmit()} />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Заметка</label>
            <select value={newNoteId} onChange={(e) => setNewNoteId(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none">
              <option value="">—</option>
              {noteOptions.map(n => (
                <option key={n.id} value={n.id}>{n.title}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Повтор</label>
          <div className="flex gap-2">
            {(['none', 'daily', 'weekly', 'monthly'] as const).map(v => (
              <button key={v} onClick={() => setNewRecurring(v)}
                className={`flex-1 py-2 text-xs rounded-xl border transition-colors ${newRecurring === v ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400' : 'border-slate-600 text-slate-500'}`}>
                {v === 'none' ? 'Нет' : v === 'daily' ? 'День' : v === 'weekly' ? 'Неделя' : 'Месяц'}
              </button>
            ))}
          </div>
        </div>
        <button onClick={onSubmit} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors font-medium">
          Добавить запись
        </button>
      </div>
    </div>
  );
}
